import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileText, Plus, Pencil, Trash2, Sparkles, Bell, Loader2, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { value: "sop",          label: "SOP" },
  { value: "job_sheet",    label: "Job Sheet" },
  { value: "safety_sheet", label: "Safety Sheet" },
  { value: "training",     label: "Training" },
];

const REMINDER_TYPE_LABELS = {
  training_due: "Training due",
  cert_expiry: "Certificate expiry",
  safety_signoff: "Safety sign-off outstanding",
  timesheet_submission: "Timesheet submission",
  payroll_run_due: "Payroll run due",
  appointment_24h: "Appointment in 24 h",
  appointment_1h: "Appointment in 1 h",
  review_request: "Post-job review request",
  job_cost_threshold: "Job cost threshold crossed",
};

const blank = () => ({
  doc_type: "sop", title: "", category: "", content_md: "",
  auto_create_with_jobs: false, is_active: true,
  channels: { portal: true, email: false, job_chat: false, use_job_id_subject: false },
  auto_save: { job: true, customer: false, extract_notes_for_quoting: false },
  visibility: { employee: true, client: false },
});

export default function OperationalDocs() {
  const [tab, setTab] = useState("docs");
  const [filter, setFilter] = useState("all");
  const [docs, setDocs] = useState([]);
  const [reminders, setReminders] = useState({ types: [], audiences: [], configs: [] });
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMode, setAiMode] = useState("polish");
  const [aiBusy, setAiBusy] = useState(false);

  const refresh = async () => {
    try {
      const url = filter === "all" ? `${API}/operational-docs?include_inactive=true` : `${API}/operational-docs?doc_type=${filter}&include_inactive=true`;
      const [d, r] = await Promise.all([axios.get(url), axios.get(`${API}/reminders/configs`)]);
      setDocs(d.data || []);
      setReminders(r.data || { types: [], audiences: [], configs: [] });
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { refresh(); }, [filter]);

  const save = async () => {
    if (!edit?.title) { toast.error("Title required"); return; }
    try {
      if (edit.id) await axios.put(`${API}/operational-docs/${edit.id}`, edit);
      else await axios.post(`${API}/operational-docs`, edit);
      toast.success("Saved"); setOpen(false); setEdit(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this doc?")) return;
    try { await axios.delete(`${API}/operational-docs/${id}`); toast.success("Deactivated"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const runAI = async () => {
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/operational-docs/ai-assist`, {
        doc_type: edit?.doc_type, mode: aiMode,
        title: edit?.title, existing_content: edit?.content_md || "",
      });
      setEdit({ ...edit, content_md: r.data.content_md || edit.content_md });
      toast.success(`AI ${aiMode} complete`);
      setAiOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI failed"); }
    finally { setAiBusy(false); }
  };

  // Reminder matrix helpers — find/upsert by (type, audience).
  const cfgFor = (type, aud) => reminders.configs.find((c) => c.reminder_type === type && c.audience === aud);
  const toggleReminder = async (type, aud, currentEnabled) => {
    const existing = cfgFor(type, aud) || {};
    try {
      await axios.put(`${API}/reminders/configs`, {
        reminder_type: type, audience: aud,
        is_enabled: !currentEnabled,
        lead_time_days: existing.lead_time_days ?? 7,
        cadence_days: existing.cadence_days ?? 1,
        channel_email: existing.channels?.email ?? true,
        channel_portal: existing.channels?.portal ?? true,
        channel_dashboard: existing.channels?.dashboard ?? true,
      });
      refresh();
    } catch { toast.error("Toggle failed"); }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="operational-docs-page">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-lg bg-primary/10"><FileText className="h-7 w-7 text-primary" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Operational Docs &amp; Reminders</h1>
          <p className="text-muted-foreground">SOPs, job sheets, safety sheets and trainings — plus the reminder engine that nudges everyone.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="docs" data-testid="tab-docs"><FileText className="h-4 w-4 mr-1" /> Docs</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders"><Bell className="h-4 w-4 mr-1" /> Reminders</TabsTrigger>
        </TabsList>

        {/* ===== DOCS ===== */}
        <TabsContent value="docs">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center gap-3 flex-wrap">
                <div><CardTitle>{docs.length} docs</CardTitle><CardDescription>Choose a type filter, or add a new one.</CardDescription></div>
                <div className="flex items-center gap-2">
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-48" data-testid="docs-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => { setEdit(blank()); setOpen(true); }} data-testid="add-doc-btn"><Plus className="h-4 w-4 mr-1" /> New doc</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead><TableHead>Type</TableHead>
                    <TableHead>Channels</TableHead><TableHead>Auto</TableHead>
                    <TableHead>Visibility</TableHead><TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docs.map((d) => (
                    <TableRow key={d.id} className={!d.is_active ? "opacity-50" : ""}>
                      <TableCell className="font-medium">{d.title}{d.category && <Badge variant="outline" className="ml-2 text-xs">{d.category}</Badge>}</TableCell>
                      <TableCell><Badge variant="secondary">{DOC_TYPES.find((t) => t.value === d.doc_type)?.label || d.doc_type}</Badge></TableCell>
                      <TableCell className="text-xs space-x-1">
                        {d.channels?.portal && <Badge variant="outline">Portal</Badge>}
                        {d.channels?.email && <Badge variant="outline">Email</Badge>}
                        {d.channels?.job_chat && <Badge variant="outline">Job chat</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{d.auto_create_with_jobs ? <Badge variant="default">Auto</Badge> : <Badge variant="outline">Manual</Badge>}</TableCell>
                      <TableCell className="text-xs">
                        {d.visibility?.employee && <Badge variant="outline" className="mr-1">Staff</Badge>}
                        {d.visibility?.client && <Badge variant="outline">Client</Badge>}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEdit({ ...d }); setOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(d.id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {docs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No docs yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== REMINDERS MATRIX ===== */}
        <TabsContent value="reminders">
          <Card>
            <CardHeader><CardTitle>Reminder configuration</CardTitle><CardDescription>Toggle each reminder per audience. Click a cell to enable/disable. Edit details from the row.</CardDescription></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reminder</TableHead>
                    {reminders.audiences.map((aud) => <TableHead key={aud} className="text-center capitalize">{aud}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reminders.types.map((t) => (
                    <TableRow key={t}>
                      <TableCell className="font-medium">{REMINDER_TYPE_LABELS[t] || t}</TableCell>
                      {reminders.audiences.map((aud) => {
                        const c = cfgFor(t, aud);
                        const on = !!c?.is_enabled;
                        return (
                          <TableCell key={aud} className="text-center">
                            <button onClick={() => toggleReminder(t, aud, on)}
                              className={`px-3 py-1 rounded text-xs font-medium ${on ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
                              data-testid={`reminder-${t}-${aud}`}>
                              {on ? `ON · ${c?.lead_time_days}d lead` : "Off"}
                            </button>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground italic mt-3">Tip: for fine-tuning lead-time / cadence / channels per cell, click into the corresponding feature page (e.g. Trainings, Certifications, Payroll).</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Doc editor */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEdit(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{edit?.id ? "Edit doc" : "New doc"}</DialogTitle>
            <DialogDescription>SOPs, job sheets, safety sheets and trainings all share this editor. Toggle channels and auto-behavior below.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid gap-3 max-h-[70vh] overflow-auto">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={edit.doc_type} onValueChange={(v) => setEdit({ ...edit, doc_type: v })} disabled={!!edit.id}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Title</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="doc-title" /></div>
              </div>
              <div><Label>Category (job-type filter, optional)</Label><Input value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} /></div>
              <div>
                <div className="flex justify-between items-center">
                  <Label>Content (Markdown)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => setAiOpen(true)} data-testid="ai-assist-btn">
                    <Wand2 className="h-3.5 w-3.5 mr-1" /> AI assist (optional)
                  </Button>
                </div>
                <Textarea rows={10} className="font-mono text-xs" value={edit.content_md || ""} onChange={(e) => setEdit({ ...edit, content_md: e.target.value })} />
              </div>

              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auto-create &amp; send</p>
                <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={edit.auto_create_with_jobs} onCheckedChange={(v) => setEdit({ ...edit, auto_create_with_jobs: !!v })} /> Auto-create this doc with new jobs that match the category</label>
                <div className="grid grid-cols-4 gap-2 text-sm pt-1">
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.channels?.portal} onCheckedChange={(v) => setEdit({ ...edit, channels: { ...edit.channels, portal: !!v } })} /> Portal</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.channels?.email} onCheckedChange={(v) => setEdit({ ...edit, channels: { ...edit.channels, email: !!v } })} /> Email</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.channels?.job_chat} onCheckedChange={(v) => setEdit({ ...edit, channels: { ...edit.channels, job_chat: !!v } })} /> Job chat</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.channels?.use_job_id_subject} onCheckedChange={(v) => setEdit({ ...edit, channels: { ...edit.channels, use_job_id_subject: !!v } })} /> Use job # in subject</label>
                </div>
              </div>
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auto-save destinations</p>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.auto_save?.job} onCheckedChange={(v) => setEdit({ ...edit, auto_save: { ...edit.auto_save, job: !!v } })} /> Save to job record</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.auto_save?.customer} onCheckedChange={(v) => setEdit({ ...edit, auto_save: { ...edit.auto_save, customer: !!v } })} /> Save to customer record</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.auto_save?.extract_notes_for_quoting} onCheckedChange={(v) => setEdit({ ...edit, auto_save: { ...edit.auto_save, extract_notes_for_quoting: !!v } })} /> Extract notes for future quoting</label>
                </div>
              </div>
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Portal visibility</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.visibility?.employee} onCheckedChange={(v) => setEdit({ ...edit, visibility: { ...edit.visibility, employee: !!v } })} /> Show on employee portal</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={edit.visibility?.client} onCheckedChange={(v) => setEdit({ ...edit, visibility: { ...edit.visibility, client: !!v } })} /> Show on client portal</label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-doc-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI assist sub-dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> AI assist (optional)</DialogTitle>
            <DialogDescription>Runs only when you click below. Output replaces the current draft.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <Label>Mode</Label>
            <Select value={aiMode} onValueChange={setAiMode}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft from scratch</SelectItem>
                <SelectItem value="polish">Polish &amp; tighten wording</SelectItem>
                <SelectItem value="proofread">Proofread (grammar / tone)</SelectItem>
                <SelectItem value="add_clauses">Add standard CRA / WSIB / liability clauses</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Uses Claude Sonnet 4.5 via your Emergent LLM key. Always review the result.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>Cancel</Button>
            <Button onClick={runAI} disabled={aiBusy}>{aiBusy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
